<template>
  <div class="maintenance-page">
    <magazine-hero
      :back-to="backTo"
      :title="page.title"
      :category="exists(page.categories) ? getFirst(page.categories) : null"
      :description="page.description" />

    <div
      v-if="exists(related)"
      class="maintenance-page__related">
      <div class="l-wrapper">
        <div class="maintenance-page__title">
          {{ $t('general.mightInterestYou') }}
        </div>
      </div>

      <div class="maintenance-list">
        <ul>
          <li
            v-for="article in related"
            :key="article.id">
            <div class="maintenance-card">
              <div
                v-if="exists(article.image)"
                class="maintenance-card__image">
                <nuxt-link
                  :to="localePath(
                    exists(article.categories) ? {
                      name: 'maintenance-category-slug',
                      params: {
                        slug: article.slug,
                        category: getFirst(article.categories).slug,
                      },
                    } : { name: 'maintenance' },
                  )">
                  <base-image v-bind="getFirst(article.image)" />
                </nuxt-link>
              </div>
              <div class="maintenance-card__title">
                {{ article.title }}
              </div>
              <div class="maintenance-card__abstract">
                {{ article.abstract }}
              </div>
              <div class="maintenance-card__link">
                <cta
                  variant="text"
                  :text="$t('actions.learnMore')"
                  :to="localePath(
                    exists(article.categories) ? {
                      name: 'maintenance-category-slug',
                      params: {
                        slug: article.slug,
                        category: getFirst(article.categories).slug,
                      },
                    } : { name: 'maintenance' },
                  )">
                  {{ $t('actions.learnMore') }}
                </cta>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
  import transitions from '~/animations/pages'
  import { useBackNavigation } from '~/composables/useBackNavigation'
  import { MAINTENANCE, MAINTENANCE_DETAIL } from '~/graphql/pages/listing'

  const { locale, localeCodes } = useI18n()
  const { params } = useRoute()
  const localePath = useLocalePath()

  // Smart back navigation - returns to previous page or maintenance index
  const { backTo } = useBackNavigation({ name: 'maintenance' })
  const {
    previewToken, previewParam, previewTimestamp
  } = usePreview()

  const { data: page, error } = await useAsyncData(
    `maintenance-${params?.slug}-${locale.value}`,
    () => useApi(MAINTENANCE_DETAIL, {
      slug: params?.slug,
      category: params?.category
    }, {
      previewToken: previewToken.value,
      previewParam: previewParam.value
    }), {
      transform: r => r.page,
      watch: [ previewTimestamp ]
    })

  let related = ref(page?.value?.related)

  const category = computed(() => {
    return exists(page?.value?.categories) ? getFirst(page?.value?.categories) : null
  })

  if (!exists(page?.value?.related)) {
    const { data } = await useAsyncData(
      `maintenance-${page.value.id}-${locale.value}`,
      () => useApi(MAINTENANCE, {
        excludedIds: page?.value?.id,
        categories: [ category?.value?.id ],
        limit: 3
      }), {
        transform: r => ({
          articles: r.articles,
          categories: r.categories
        })
      })
    related = ref(data?.value?.articles?.sort((a, b) => a.title.match(/[^n°]*$/g)[0] - b.title.match(/[^n°]*$/g)[0]))
  }

  // Handle fetch errors
  if (error.value || !page.value) {
    console.error('Error fetching maintenance article data:', error.value)

    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found'
    })
  }

  // SEO Meta
  useSeoData(page)

  const setI18nParams = useSetI18nParams()
  setI18nParams(localeCodes.value.reduce((acc, _locale) => {
    if (_locale == locale.value) {
      acc[_locale] = {
        category: params.category,
        slug: params.slug
      }
    } else {
      acc[_locale] = {
        category: category.value.localized.find(c => c.site == _locale)?.slug ?? null,
        slug: page.value?.localized?.find(lp => lp.site == _locale)?.slug ?? null
      }
    }
    return acc
  }, {}))

  definePageMeta({
    pageTransition: {
      name: 'page-transition',
      mode: 'out-in',
      ...transitions
    }
  })
</script>

<style lang="scss" scoped>
  .maintenance-page {
    &__related {
      @include blocks-y-spacing;
    }

    &__title {
      @include title-xl;

      text-align: center;
      text-transform: uppercase;
      margin-bottom: rem(40px);
    }
  }
</style>
